import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadSimple, ArrowLeft, Trash, FilePdf } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";

const FOLDER_LABELS = {
  pitch_deck: "Pitch Deck",
  financial_model: "Financial Model",
  financial_statements: "Financial Statements",
  cap_table: "Cap Table",
  legal: "Legal Documents",
  due_diligence: "Due Diligence",
  product_demo: "Product Demo",
  other: "Other Documents",
};

export default function AdminCompanyDocuments() {
  const { id } = useParams();
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  const [docs, setDocs] = useState([]);
  const [folder, setFolder] = useState("pitch_deck");
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    api.get(`/companies/${id}`).then((r) => setCompany(r.data));
    api.get(`/companies/${id}/documents`).then((r) => setDocs(r.data));
  };
  useEffect(() => { load(); }, [id]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", folder);
        await api.post(`/companies/${id}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file(s) uploaded`);
      load();
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async (docId) => {
    await api.delete(`/documents/${docId}`);
    toast.success("Deleted");
    load();
  };

  const grouped = docs.reduce((acc, d) => { (acc[d.folder] ||= []).push(d); return acc; }, {});

  if (!company) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"><ArrowLeft size={14} /> Back</button>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Data Room · {company.name}</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Documents</h1>
        </div>
        <Link to={`/companies/${id}`} className="text-sm text-emerald-700 dark:text-emerald-400 hover:underline">View investor page →</Link>
      </div>

      <div className="rounded-lg border border-border bg-card p-6 mb-6">
        <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-3">Upload documents</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Folder</label>
            <Select value={folder} onValueChange={setFolder}>
              <SelectTrigger data-testid="upload-folder-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(FOLDER_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          className={`rounded-md border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragging ? "border-emerald-600 bg-emerald-50 dark:bg-emerald-950/20" : "border-border hover:border-emerald-500"}`}
          data-testid="upload-dropzone"
        >
          <UploadSimple size={28} className="mx-auto text-muted-foreground mb-2" />
          <div className="font-medium">Drop files or click to upload</div>
          <div className="text-xs text-muted-foreground mt-1">PDF, images, videos. Multiple files supported.</div>
          {uploading && <div className="mt-2 text-xs text-emerald-700">Uploading…</div>}
          <input ref={inputRef} type="file" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="upload-input" />
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(FOLDER_LABELS).map(([f, label]) => {
          const list = grouped[f] || [];
          return (
            <div key={f} className="rounded-lg border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display font-semibold">{label}</div>
                <div className="text-xs text-muted-foreground">{list.length} document{list.length === 1 ? "" : "s"}</div>
              </div>
              {list.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">No documents.</div>
              ) : (
                <ul className="space-y-1.5">
                  {list.map((d) => (
                    <li key={d.id} className="flex items-center gap-3 p-2 rounded hover:bg-secondary/40">
                      <FilePdf size={18} weight="fill" className="text-red-600" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{d.name}</div>
                        <div className="text-[11px] text-muted-foreground">{(d.size / 1024).toFixed(0)} KB · {formatDate(d.uploaded_at)}</div>
                      </div>
                      <button onClick={() => remove(d.id)} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 p-1.5 rounded" data-testid={`delete-doc-${d.id}`}>
                        <Trash size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
